import { useRef, useState, useEffect, useCallback, useMemo, type ComponentProps } from 'react'
import { twMerge } from 'tailwind-merge'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useCellContext } from './context'
import { Button } from '../../button'

const FADE = '36px'

function getMask(left: boolean, right: boolean): string | undefined {
  if (left && right)
    return `linear-gradient(to right, transparent, black ${FADE}, black calc(100% - ${FADE}), transparent)`
  if (left) return `linear-gradient(to right, transparent, black ${FADE})`
  if (right) return `linear-gradient(to left, transparent, black ${FADE})`
  return undefined
}

interface CellMetadataProps extends ComponentProps<'div'> {
  /** When false, overflow fades but scroll buttons are hidden. Default true. */
  scrollable?: boolean
}

function CellMetadata({ className, children, scrollable = true, ...props }: CellMetadataProps) {
  const { size } = useCellContext()
  const gap = size === 'compact' ? 'gap-1.5' : 'gap-2'

  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateOverflow = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    updateOverflow()

    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const debouncedUpdate = () => {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(updateOverflow, 50)
    }

    const resizeObserver = new ResizeObserver(debouncedUpdate)
    const mutationObserver = new MutationObserver(debouncedUpdate)

    resizeObserver.observe(el)
    mutationObserver.observe(el, {
      childList: true,
      subtree: true,
      attributes: true,
    })

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [updateOverflow])

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return
    const amount = el.clientWidth * 0.6
    el.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    })
  }

  const mask = useMemo(() => getMask(canScrollLeft, canScrollRight), [canScrollLeft, canScrollRight])

  return (
    <div data-slot="cell-metadata" className="relative -ml-0.5 basis-full">
      <div
        ref={scrollRef}
        onScroll={scrollable ? updateOverflow : undefined}
        className={twMerge('flex overflow-x-hidden', gap, className)}
        style={{ maskImage: mask, WebkitMaskImage: mask }}
        {...props}
      >
        {children}
      </div>

      {scrollable && canScrollLeft && (
        <Button
          type="button"
          aria-label="Scroll left"
          size="xs"
          shape="square"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation()
            scroll('left')
          }}
          className="text-foreground-secondary absolute -left-1 -top-px"
        >
          <ChevronLeft />
        </Button>
      )}

      {scrollable && canScrollRight && (
        <Button
          type="button"
          aria-label="Scroll right"
          onClick={(e) => {
            e.stopPropagation()
            scroll('right')
          }}
          size="xs"
          shape="square"
          variant="ghost"
          className="text-foreground-secondary absolute -right-1 -top-px"
        >
          <ChevronRight />
        </Button>
      )}
    </div>
  )
}

export { CellMetadata }
